// src/admin/pages/Dashboard/components/process/ProcessChartSection.js
import React, { useState } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from "chart.js";
import { Bar } from "react-chartjs-2";

// Registriamo i componenti base di Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

/**
 * Props:
 *   dailyMetrics, dishMetrics, weeklyMetrics, monthlyMetrics, yearlyMetrics, hourlyMetrics
 *   dayOfWeek => "all" o "0..6"
 *   onChangeDayOfWeek => callback(newVal)
 */
export default function ProcessChartSection({
  dailyMetrics,
  dishMetrics,
  weeklyMetrics,
  monthlyMetrics,
  yearlyMetrics,
  hourlyMetrics,
  dayOfWeek = "all",
  onChangeDayOfWeek,
}) {
  // chartType: "daily", "dish", "weekly", "monthly", "yearly", "hourly"
  const [chartType, setChartType] = useState("daily");
  // metric: "queue", "prep", "delivery", "total"
  const [metric, setMetric] = useState("prep");

  // Costruiamo { labels, dataValues, title }
  const { labels, dataValues, title } = buildChartData(
    chartType,
    metric,
    dailyMetrics,
    dishMetrics,
    weeklyMetrics,
    monthlyMetrics,
    yearlyMetrics,
    hourlyMetrics
  );

  const noData = !labels || labels.length === 0;

  return (
    <div style={styles.container}>
      {/* Sezione filtri (chartType, metric, dayOfWeek) */}
      <div style={styles.filterCard}>
        <div style={styles.selectContainer}>
          <ChartSelectors
            chartType={chartType}
            setChartType={setChartType}
            metric={metric}
            setMetric={setMetric}
          />

          {/* Se chartType=hourly, mostriamo il selettore dayOfWeek */}
          {chartType === "hourly" && (
            <div style={styles.field}>
              <label style={styles.label}>Giorno settimana</label>
              <select
                style={styles.select}
                value={dayOfWeek}
                onChange={(e) => onChangeDayOfWeek(e.target.value)}
              >
                <option value="all">All Days</option>
                <option value="0">Monday</option>
                <option value="1">Tuesday</option>
                <option value="2">Wednesday</option>
                <option value="3">Thursday</option>
                <option value="4">Friday</option>
                <option value="5">Saturday</option>
                <option value="6">Sunday</option>
              </select>
            </div>
          )}
        </div>
      </div>

      {/* Se non ci sono dati, messaggio; altrimenti il Bar chart */}
      {noData ? (
        <p style={styles.noDataText}>Nessun dato disponibile per questo grafico.</p>
      ) : (
        <div style={styles.chartContainer}>
          <Bar
            data={{
              labels,
              datasets: [
                {
                  label: title,
                  data: dataValues,
                  backgroundColor: "rgba(255, 102, 0, 0.7)",
                },
              ],
            }}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              plugins: {
                title: {
                  display: true,
                  text: title,
                },
                tooltip: {
                  callbacks: {
                    label: (context) => {
                      const val = context.parsed.y;
                      return `${val.toFixed(1)} min`;
                    },
                  },
                },
              },
              scales: {
                y: { beginAtZero: true },
              },
            }}
          />
        </div>
      )}
    </div>
  );
}

/** Selettori "Tipo di Grafico" e "Metrica" in stile più moderno */
function ChartSelectors({ chartType, setChartType, metric, setMetric }) {
  return (
    <div style={styles.selectorGroup}>
      <div style={styles.field}>
        <label style={styles.label}>Tipo di Grafico</label>
        <select
          style={styles.select}
          value={chartType}
          onChange={(e) => setChartType(e.target.value)}
        >
          <option value="daily">Daily</option>
          <option value="dish">Dish</option>
          <option value="weekly">Weekly</option>
          <option value="monthly">Monthly</option>
          <option value="yearly">Yearly</option>
          <option value="hourly">Hourly</option>
        </select>
      </div>
      <div style={styles.field}>
        <label style={styles.label}>Metrica</label>
        <select
          style={styles.select}
          value={metric}
          onChange={(e) => setMetric(e.target.value)}
        >
          <option value="queue">Queue</option>
          <option value="prep">Prep</option>
          <option value="delivery">Delivery</option>
          <option value="total">Total</option>
        </select>
      </div>
    </div>
  );
}

/** Funzione per costruire i dati del grafico */
function buildChartData(
  chartType,
  metric,
  dailyArr,
  dishArr,
  weeklyArr,
  monthlyArr,
  yearlyArr,
  hourlyArr
) {
  let labels = [];
  let dataValues = [];
  let title = "";
  const metricKey = `avg_${metric}_sec`;

  if (chartType === "daily") {
    if (!dailyArr || dailyArr.length === 0) return { labels, dataValues, title };
    labels = dailyArr.map((d) => d.day);
    dataValues = dailyArr.map((d) => {
      const sec = d[metricKey] || 0;
      return parseFloat((sec / 60).toFixed(1));
    });
    title = `Daily Average ${metric} (min)`;
  } else if (chartType === "dish") {
    if (!dishArr || dishArr.length === 0) return { labels, dataValues, title };
    labels = dishArr.map((x) => x.menu_item_name);
    dataValues = dishArr.map((x) => {
      const sec = x[metricKey] || 0;
      return parseFloat((sec / 60).toFixed(1));
    });
    title = `Average ${metric} per Dish (min)`;
  } else if (chartType === "weekly") {
    if (!weeklyArr || weeklyArr.length === 0) return { labels, dataValues, title };
    labels = weeklyArr.map((x) => x.weekday_label);
    dataValues = weeklyArr.map((x) => {
      const sec = x[metricKey] || 0;
      return parseFloat((sec / 60).toFixed(1));
    });
    title = `Weekly Average ${metric} (min)`;
  } else if (chartType === "monthly") {
    if (!monthlyArr || monthlyArr.length === 0) return { labels, dataValues, title };
    labels = monthlyArr.map((m) => m.month_str);
    dataValues = monthlyArr.map((m) => {
      const sec = m[metricKey] || 0;
      return parseFloat((sec / 60).toFixed(1));
    });
    title = `Monthly Average ${metric} (min)`;
  } else if (chartType === "yearly") {
    if (!yearlyArr || yearlyArr.length === 0) return { labels, dataValues, title };
    labels = yearlyArr.map((y) => y.year_str);
    dataValues = yearlyArr.map((y) => {
      const sec = y[metricKey] || 0;
      return parseFloat((sec / 60).toFixed(1));
    });
    title = `Yearly Average ${metric} (min)`;
  } else if (chartType === "hourly") {
    if (!hourlyArr || hourlyArr.length === 0) return { labels, dataValues, title };
    // hour_of_day => 0..23
    labels = hourlyArr.map((h) => {
      const hh = String(h.hour_of_day).padStart(2, "0");
      return `${hh}:00`;
    });
    dataValues = hourlyArr.map((h) => {
      const sec = h[metricKey] || 0;
      return parseFloat((sec / 60).toFixed(1));
    });
    title = `Hourly Average ${metric} (min)`;
  }

  return { labels, dataValues, title };
}

// Stili
const styles = {
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "1.5rem",
    padding: "1rem",
    backgroundColor: "#fdfdfd",
    borderRadius: "8px",
    boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
  },
  filterCard: {
    backgroundColor: "#fff",
    borderRadius: "8px",
    padding: "1rem",
    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
  },
  selectContainer: {
    display: "flex",
    flexWrap: "wrap",
    gap: "1.5rem",
  },
  selectorGroup: {
    display: "flex",
    flexWrap: "wrap",
    gap: "1rem",
  },
  field: {
    display: "flex",
    flexDirection: "column",
    minWidth: "120px",
  },
  label: {
    fontSize: "0.9rem",
    marginBottom: "0.3rem",
    color: "#333",
    fontWeight: "bold",
  },
  select: {
    padding: "0.4rem 0.6rem",
    borderRadius: "4px",
    border: "1px solid #ccc",
    fontSize: "0.9rem",
  },
  noDataText: {
    marginTop: "1rem",
    color: "#777",
    fontStyle: "italic",
  },
  chartContainer: {
    width: "100%",
    height: "400px",
  },
};
